import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ShieldCheck, CheckCircle2, MailCheck, PhoneCall, RefreshCw } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import VerifyContactPanel from '@/components/VerifyContactPanel';
import { fetchVerificationGate, EMPTY_GATE, type VerificationGate } from '@/lib/homeownerTrust';

/**
 * Shows homeowners which projects are waiting for verification and lets them finish it here.
 * The server decides when a project goes live: mkt_my_verification_gate publishes waiting
 * projects (after a risk re-score) once the email and phone are both verified.
 */
const HomeownerGoLiveBanner: React.FC<{ refreshKey?: number; onPromoted?: () => void }> = ({ refreshKey = 0, onPromoted }) => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const [gate, setGate] = useState<VerificationGate>(EMPTY_GATE);
  const [showVerify, setShowVerify] = useState(false);
  const [busy, setBusy] = useState(false);
  // Keep the latest callback in a ref so a new parent function each render never re-triggers the check.
  const promotedRef = useRef(onPromoted);
  promotedRef.current = onPromoted;

  const check = useCallback(async () => {
    setBusy(true);
    const g = await fetchVerificationGate();
    setGate(g);
    setBusy(false);
    if (g.promoted > 0) promotedRef.current?.();
  }, []);

  useEffect(() => { void check(); }, [check, refreshKey]);

  if (!gate.ok) return null;

  if (gate.promoted > 0) {
    return (
      <p role="status" className="mt-6 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        {es
          ? `¡Cuenta verificada! ${gate.promoted} proyecto(s) enviado(s) para publicación.`
          : `Account verified! ${gate.promoted} project(s) submitted to go live.`}
      </p>
    );
  }

  if (gate.pending === 0 || gate.canGoLive) return null;

  const Step: React.FC<{ done: boolean; icon: React.ReactNode; label: string }> = ({ done, icon, label }) => (
    <li className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold ${done ? 'bg-emerald-50 text-emerald-800' : 'bg-white text-slate-700 ring-1 ring-amber-200'}`}>
      {done ? <CheckCircle2 className="h-4 w-4" /> : icon} {label}
    </li>
  );

  return (
    <section className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
      <h3 className="flex items-center gap-2 text-base font-extrabold text-amber-900">
        <ShieldCheck className="h-5 w-5" />
        {es
          ? `${gate.pending} proyecto(s) esperando su verificación`
          : `${gate.pending} project(s) waiting for you to verify`}
      </h3>
      <p className="mt-1 text-sm text-amber-900">
        {es
          ? 'Sus proyectos están guardados pero los contratistas aún no los ven. Verifique su correo y su teléfono una sola vez y se publicarán automáticamente.'
          : 'Your projects are saved, but contractors cannot see them yet. Verify your email and phone once and they go live automatically.'}
      </p>
      <ul className="mt-3 grid gap-2 sm:grid-cols-2">
        <Step done={gate.emailVerified} icon={<MailCheck className="h-4 w-4" />} label={es ? 'Correo verificado' : 'Email verified'} />
        <Step done={gate.phoneVerified} icon={<PhoneCall className="h-4 w-4" />} label={es ? 'Teléfono verificado (código SMS)' : 'Phone verified (SMS code)'} />
      </ul>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() => setShowVerify((v) => !v)}
          className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-800"
        >
          {showVerify ? (es ? 'Ocultar' : 'Hide') : es ? 'Verificar ahora' : 'Verify now'}
        </button>
        <button
          onClick={() => void check()}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-white px-4 py-2.5 text-sm font-bold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> {es ? 'Ya verifiqué' : "I've verified"}
        </button>
      </div>
      {showVerify && (
        <div className="mt-4">
          <VerifyContactPanel />
        </div>
      )}
    </section>
  );
};

export default HomeownerGoLiveBanner;
